"use client";

/**
 * `FormProvider` — React context bundling the rhf instance, the zod
 * schema, and the declarative `fields` config so descendant field
 * components can render themselves without prop-drilling.
 *
 * We could lean on `react-hook-form`'s own `FormProvider` for the rhf
 * piece, but the engine also needs the schema (for cross-field
 * refinement messaging) and the fields config (for the renderer to
 * dispatch). Bundling all three in one context keeps composition
 * trivial: `<FormProvider form schema fields>{...}</FormProvider>`.
 *
 * The `<Form>` composer (Phase J) is the canonical wrapper; this
 * provider is exposed publicly only so advanced callers who want to
 * lay out their form manually can do so.
 */

import * as React from "react";
import type { FieldValues } from "react-hook-form";
import type { z } from "zod";
import type { UseFormResult } from "./use-form";
import type { FieldDef } from "./types";

/**
 * Shape stored in the context. `unknown` is used for `TValues` because
 * the context is generic over any caller's value type but React
 * contexts don't carry generics; consumers narrow at the use site via
 * `useFormContext<TValues>()`.
 */
export interface FormContextValue<TValues extends FieldValues = FieldValues> {
  /** The full rhf instance + our extras. */
  form: UseFormResult<TValues>;
  /** The zod schema driving validation. */
  schema: z.ZodType<TValues>;
  /** The declarative fields config. */
  fields: FieldDef[];
}

const FormCtx = React.createContext<FormContextValue<FieldValues> | null>(null);

/**
 * Props for `<FormProvider>`. Every prop is required — callers must
 * have a form, a schema, and a fields config to provide meaningful
 * context.
 */
export interface FormProviderProps<TValues extends FieldValues> {
  form: UseFormResult<TValues>;
  schema: z.ZodType<TValues>;
  fields: FieldDef[];
  children: React.ReactNode;
}

/**
 * Wraps descendants with the form context. Use directly when composing
 * a custom form layout; the canonical `<Form>` composer wraps it for
 * you.
 */
export function FormProvider<TValues extends FieldValues>({
  form,
  schema,
  fields,
  children,
}: FormProviderProps<TValues>): React.ReactElement {
  // Cast to the erased context value shape — the consumer-side hook
  // re-introduces the generic.
  const value = React.useMemo<FormContextValue<FieldValues>>(
    () => ({
      form: form as unknown as UseFormResult<FieldValues>,
      schema: schema as unknown as z.ZodType<FieldValues>,
      fields,
    }),
    [form, schema, fields],
  );
  return <FormCtx.Provider value={value}>{children}</FormCtx.Provider>;
}

/**
 * Read the form context. Throws if used outside a `<FormProvider>` —
 * silent fallbacks here would mask wiring mistakes.
 *
 * `TValues` is informational; descendants must already know what shape
 * they expect.
 */
export function useFormContext<
  TValues extends FieldValues = FieldValues,
>(): FormContextValue<TValues> {
  const ctx = React.useContext(FormCtx);
  if (!ctx) {
    throw new Error("useFormContext must be used inside <FormProvider>");
  }
  return ctx as unknown as FormContextValue<TValues>;
}
